/**
 * Verify Email Page - Client-Side Handler
 *
 * Handles email verification using Firebase's applyActionCode() method.
 * This follows Firebase's recommended pattern for custom email handlers.
 *
 * Flow:
 * 1. User clicks verification link in email
 * 2. Firebase redirects to this page with oobCode and mode parameters
 * 3. Page calls Firebase applyActionCode() to verify email (ONCE)
 * 4. Page syncs verification status to database
 * 5. Displays success/error message with branded UI
 * 6. On "Continue" button click, refreshes AuthContext before navigation
 *    to ensure email verification banner doesn't show with stale state
 *
 * CRITICAL: This component ensures verification logic runs exactly ONCE per mount
 * to prevent "invalid-action-code" errors from duplicate verification attempts.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import { useAuth } from '../contexts/AuthContext';

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUserVerificationStatus } = useAuth();
  const [state, setState] = useState<'verifying' | 'success' | 'error' | 'password-reset-form'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submittingPassword, setSubmittingPassword] = useState(false);

  // Single execution guard - persists across re-renders and StrictMode
  const executedRef = useRef(false);

  // Freeze URL params on first mount - prevents issues with searchParams changing
  const frozenParams = useRef({
    mode: searchParams.get('mode'),
    oobCode: searchParams.get('oobCode'),
  });

  const correlationId = useMemo(() => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `verify-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  }, []);

  const logEvent = (stage: string, details: Record<string, unknown> = {}) => {
    console.log('[verify-email]', {
      op: 'verify-email',
      stage,
      correlationId,
      ...details,
    });
  };

  useEffect(() => {
    // Guard: Prevent multiple executions (StrictMode, re-renders, auth state changes)
    if (executedRef.current) {
      logEvent('skip-duplicate', { reason: 'already-executed' });
      return;
    }

    executedRef.current = true;

    const handleVerifyEmail = async () => {
      // Use frozen params from mount, not live searchParams
      const { mode, oobCode: actionCode } = frozenParams.current;

      logEvent('start', {
        mode,
        hasActionCode: Boolean(actionCode),
      });

      // Store mode for later use (e.g., password form submission)
      setMode(mode);

      // Handle password reset flow
      if (mode === 'resetPassword') {
        if (!actionCode) {
          logEvent('error', { reason: 'missing-action-code' });
          setState('error');
          setErrorMessage('Missing reset code. Please use the link from your email.');
          return;
        }

        try {
          // Verify the password reset code is valid
          logEvent('verify-password-reset-code');
          const resetEmail = await verifyPasswordResetCode(auth, actionCode);
          logEvent('verify-password-reset-code-success', { email: resetEmail });
          setEmail(resetEmail);
          setState('password-reset-form');
          return;
        } catch (error: any) {
          console.error('❌ Password reset code verification failed:', error);
          logEvent('verify-password-reset-code-error', {
            code: error?.code,
            message: error?.message,
          });

          if (error.code === 'auth/expired-action-code') {
            setState('error');
            setErrorMessage('This reset link has expired. Please request a new password reset email.');
          } else if (error.code === 'auth/invalid-action-code') {
            setState('error');
            setErrorMessage('This reset link is invalid or has already been used.');
          } else {
            setState('error');
            setErrorMessage('Failed to verify reset link. Please try again or contact support.');
          }
          return;
        }
      }

      // Validate required parameters for email verification
      if (mode !== 'verifyEmail') {
        // Check if user is already verified (graceful degradation)
        const user = auth.currentUser;
        if (user && user.emailVerified) {
          logEvent('success', { reason: 'already-verified-invalid-mode' });
          setState('success');
          setEmail(user.email || '');
          return;
        }

        logEvent('error', { reason: 'invalid-mode', mode });
        setState('error');
        setErrorMessage('Invalid verification request. Please check your email for the correct link.');
        return;
      }

      if (!actionCode) {
        // Check if user is already verified (graceful degradation)
        const user = auth.currentUser;
        if (user && user.emailVerified) {
          logEvent('success', { reason: 'already-verified-missing-code' });
          setState('success');
          setEmail(user.email || '');
          return;
        }

        logEvent('error', { reason: 'missing-action-code' });
        setState('error');
        setErrorMessage('Missing verification code. Please use the link from your email.');
        return;
      }

      try {
        // Step 1: Check if already verified (handles page refresh gracefully)
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.emailVerified) {
          logEvent('success', { reason: 'already-verified-before-apply' });
          setState('success');
          setEmail(currentUser.email || '');

          // Still sync to DB in case it's out of sync
          syncDatabaseVerification(currentUser.uid, currentUser.email);
          return;
        }

        // Step 2: Apply the action code to verify email
        logEvent('apply-action-code', { hasUser: Boolean(currentUser) });
        await applyActionCode(auth, actionCode);
        logEvent('apply-action-code-success');

        // Step 3: Reload user to refresh local Firebase state with emailVerified status
        // This ensures user.emailVerified reflects the verified status before redirecting
        if (currentUser) {
          logEvent('user-reload-start');
          await currentUser.reload();
          logEvent('user-reload-success', { emailVerified: currentUser.emailVerified });
        }

        // Step 4: Get user info and sync to database
        const user = auth.currentUser;
        if (user) {
          setEmail(user.email || '');

          // Sync to database (fire-and-forget, non-blocking)
          syncDatabaseVerification(user.uid, user.email);
        }

        // Step 5: Show success
        logEvent('success', { reason: 'verification-complete' });
        setState('success');

      } catch (error: any) {
        console.error('❌ Verification failed:', error);
        logEvent('apply-action-code-error', {
          code: error?.code,
          message: error?.message,
        });

        // Special handling for invalid-action-code (already used)
        if (error.code === 'auth/invalid-action-code') {
          // Check if email is actually verified despite error
          const user = auth.currentUser;
          if (user && user.emailVerified) {
            logEvent('success', { reason: 'already-verified-invalid-code' });
            setState('success');
            setEmail(user.email || '');
            return;
          }

          // Truly invalid code
          setState('error');
          setErrorMessage(
            'This verification link has already been used or is invalid. Please request a new verification email.'
          );
        } else if (error.code === 'auth/expired-action-code') {
          setState('error');
          setErrorMessage(
            'This verification link has expired. Please request a new verification email from your account settings.'
          );
        } else {
          setState('error');
          setErrorMessage('Failed to verify email. Please try again or contact support if the problem persists.');
        }
      }
    };

    handleVerifyEmail();

    // Cleanup: DO NOT reset executedRef - we want it to stay true
    // This prevents re-execution even in React StrictMode
  }, []); // Empty dependency array - run only once on mount

  // Handle password reset form submission
  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setSubmittingPassword(true);

    try {
      // Validate passwords match
      if (newPassword !== confirmPassword) {
        setPasswordError('Passwords do not match');
        setSubmittingPassword(false);
        return;
      }

      // Validate password length
      if (newPassword.length < 6) {
        setPasswordError('Password must be at least 6 characters');
        setSubmittingPassword(false);
        return;
      }

      const { oobCode: actionCode } = frozenParams.current;
      if (!actionCode) {
        throw new Error('Missing reset code');
      }

      logEvent('password-reset-submit', { email });
      await confirmPasswordReset(auth, actionCode, newPassword);
      logEvent('password-reset-success', { email });

      setState('success');
      setErrorMessage('');
      setEmail(email);
    } catch (error: any) {
      console.error('❌ Password reset failed:', error);
      logEvent('password-reset-error', {
        code: error?.code,
        message: error?.message,
      });

      if (error.code === 'auth/expired-action-code') {
        setState('error');
        setErrorMessage('This reset link has expired. Please request a new password reset email.');
      } else if (error.code === 'auth/invalid-action-code') {
        setState('error');
        setErrorMessage('This reset link is invalid or has already been used.');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('Password is too weak. Please choose a stronger password.');
      } else {
        setState('error');
        setErrorMessage('Failed to reset password. Please try again or contact support.');
      }
    } finally {
      setSubmittingPassword(false);
    }
  };

  // Background database sync (fire-and-forget, non-blocking)
  const syncDatabaseVerification = async (firebaseUid: string, email: string | null) => {
    try {
      logEvent('sync-start', { hasEmail: Boolean(email) });
      const response = await fetch('/api/auth/sync-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseUid, email }),
      });

      if (response.ok) {
        logEvent('sync-success');
      } else {
        logEvent('sync-warning', { reason: 'non-ok-response', status: response.status });
      }
    } catch (error) {
      logEvent('sync-error', { message: (error as Error).message });
      // Don't fail the verification flow if DB sync fails
    }
  };

  const handleContinue = async () => {
    // Proactively refresh auth context before navigation
    // This ensures the banner has the updated emailVerified status
    // and prevents the banner from showing with stale state
    logEvent('refresh-before-navigate');
    await refreshUserVerificationStatus();
    logEvent('refresh-complete');
    navigate('/?verified=true', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.25),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.25),transparent_30%),radial-gradient(circle_at_50%_80%,rgba(14,165,233,0.2),transparent_30%)]" />

      <div className="relative max-w-lg w-full">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img
            src="/logos/Smart Prompt Logo.png"
            alt="Smart Prompt"
            className="h-16 w-auto drop-shadow-[0_10px_40px_rgba(34,211,238,0.25)]"
          />
          <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Smart Prompt
          </span>
        </Link>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 backdrop-blur-2xl shadow-2xl"
        >
          {/* Loading/Verifying State */}
          {state === 'verifying' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6">
                <div className="w-full h-full border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
              </div>
              <h1 className="text-2xl font-bold mb-2">Verifying Your Email</h1>
              <p className="text-slate-400">Please wait while we confirm your email address...</p>
            </div>
          )}

          {/* Password Reset Form State */}
          {state === 'password-reset-form' && (
            <div>
              <div className="mb-8">
                <p className="text-sm text-slate-400 mb-2">Password Recovery</p>
                <h1 className="text-2xl font-semibold mb-2">Create a new password</h1>
                <p className="text-slate-400">
                  Enter your new password for {email}
                </p>
              </div>

              <form onSubmit={handlePasswordResetSubmit} className="space-y-4">
                {passwordError && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3">
                    <div className="text-sm font-medium">{passwordError}</div>
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="new-password" className="block text-sm font-medium text-slate-200">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={submittingPassword}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-200">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={submittingPassword}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submittingPassword || !newPassword || !confirmPassword}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/20 hover:from-cyan-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-950 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingPassword ? 'Resetting password…' : 'Reset Password'}
                </button>

                <Link
                  to="/login"
                  className="w-full flex items-center justify-center py-3 px-4 rounded-xl font-semibold text-white bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  Back to Sign In
                </Link>
              </form>
            </div>
          )}

          {/* Success State */}
          {state === 'success' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20"
              >
                <CheckCircleIcon className="w-12 h-12 text-white" />
              </motion.div>

              <h1 className="text-3xl font-bold mb-3 bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                {mode === 'resetPassword' ? 'Password Reset Successful!' : 'Email Verified!'}
              </h1>
              {mode === 'resetPassword' ? (
                <>
                  <p className="text-lg text-slate-300 mb-2">
                    Your password has been successfully reset.
                  </p>
                  <p className="text-slate-400 mb-8">
                    You can now sign in with your new password.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg text-slate-300 mb-2">
                    Your email address {email && `(${email})`} has been successfully verified.
                  </p>
                  <p className="text-slate-400 mb-8">
                    You can now access all features of Smart Prompt.
                  </p>
                </>
              )}

              <button
                onClick={handleContinue}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all hover:-translate-y-0.5"
              >
                {mode === 'resetPassword' ? 'Sign In' : 'Continue to Smart Prompt'}
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Error State */}
          {state === 'error' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/20"
              >
                <XCircleIcon className="w-12 h-12 text-white" />
              </motion.div>

              <h1 className="text-3xl font-bold mb-3 text-red-400">
                Verification Issue
              </h1>
              <p className="text-lg text-slate-300 mb-8">
                {errorMessage}
              </p>

              <div className="space-y-3">
                <Link
                  to="/"
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all hover:-translate-y-0.5"
                >
                  Go to Home
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>

                <Link
                  to="/login"
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-slate-800 text-white font-semibold border border-slate-700 hover:bg-slate-700 transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </div>
          )}
        </motion.div>

      </div>
    </div>
  );
};
